import { useRef, useState } from 'react';
import { governedStatus, settingsOrgDeletionCreate, settingsOrgDeletions, settingsPurgeSample, settingsReset, settingsRotateAllKeys, type FacilitySearchRow, type RotateAllKeysResult } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { FacilityPicker } from '../../ui/FacilityPicker';
import { ErrLine, I, chip, pre, sx, useApi, useGuarded, usePage } from './kit';

/** The organization the dialog is about, and why (the server wants both). */
function OrgFields({ onPick, onReason, onReal, withReason }: { onPick: (r: FacilitySearchRow) => void; onReason?: (s: string) => void; onReal?: (b: boolean) => void; withReason?: boolean }) {
  const [picked, setPicked] = useState<FacilitySearchRow | null>(null);
  return (
    <div style={sx('display:flex;flex-direction:column;gap:8px;margin:8px 0;')}>
      <FacilityPicker label="Organization" onPick={(r) => { setPicked(r); onPick(r); }} />
      {picked && <span style={sx('font-size:16px;color:#3C454F;')}>{'Picked: ' + picked.name}</span>}
      {withReason && (
        <label style={sx('display:block;font-size:16px;color:#3C454F;')}>
          Reason (at least 8 characters)
          <textarea rows={2} onChange={(e) => onReason?.(e.target.value)} style={sx('display:block;width:100%;box-sizing:border-box;margin-top:4px;padding:6px 9px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;')} />
        </label>
      )}
      {onReal && (
        <label style={sx('display:flex;align-items:center;gap:8px;font-size:16px;color:#3C454F;')}>
          <input type="checkbox" onChange={(e) => onReal(e.target.checked)} />
          It has real residents, and I mean to delete them too
        </label>
      )}
    </div>
  );
}

/** Danger Zone (design seIsScaffold with sec 'Danger Zone', T9971; seGuard DZ L21720-21730). Super Admin only. */
export function DangerZone() {
  const { client } = useSession();
  const { ask, lens } = usePage();
  const { canWrite, roReason } = useGuarded();
  const deletions = useApi((c, s) => settingsOrgDeletions(c, { signal: s }), []);
  const status = useApi((c, s) => governedStatus(c, { signal: s }), []);
  const [done, setDone] = useState<Record<string, string>>({});
  const [rotated, setRotated] = useState<RotateAllKeysResult | null>(null);
  const org = useRef<FacilitySearchRow | null>(null);
  const reason = useRef('');
  const real = useRef(false);

  const open = (deletions.data?.requests ?? []).find((r) => r.status === 'PENDING_SIGNATURE' || r.status === 'HOLD');
  const needOrg = () => {
    if (!org.current) throw Object.assign(new Error('Pick the organization first.'), { message: 'Pick the organization first.' });
    return org.current.id;
  };
  const ITEMS: [string, string, string, string, string, () => void][] = [
    ['Delete organization', 'Two signatures and a 30-day hold before anything is destroyed.', 'Start 30-day hold',
      open ? (open.status === 'HOLD' ? '✓ Hold started' : '✓ Awaiting signature') : done['Delete organization'] ?? '',
      'Nothing is destroyed today. A second admin must sign, then a 30-day hold runs before residents, conversations and trust ledger are removed. Truth Ledger entries survive.',
      () => ask({
        sec: 'Danger Zone', title: 'Start organization deletion?', btn: 'Start 30-day hold',
        body: 'Nothing is destroyed today. A second admin must sign, then a 30-day hold runs before residents, conversations and trust ledger are removed. Truth Ledger entries survive.',
        extra: <OrgFields withReason onPick={(r) => { org.current = r; }} onReason={(s) => { reason.current = s; }} onReal={(b) => { real.current = b; }} />,
        go: async () => {
          await settingsOrgDeletionCreate(client, { body: { organizationId: needOrg(), reason: reason.current.trim(), confirmRealResidents: real.current, confirm: true } });
          setDone((p) => ({ ...p, 'Delete organization': '✓ Awaiting signature' }));
          deletions.reload();
        },
      })],
    ['Rotate every key', 'Invalidates all integrations at once.', 'Rotate all keys', done['Rotate every key'] ?? '',
      'All integrations lose access at once and fail until each gets its new key.',
      () => ask({
        sec: 'Danger Zone', title: 'Rotate every key?', btn: 'Rotate all keys',
        body: 'All integrations lose access at once and fail until each gets its new key.',
        go: async () => {
          const tenantId = status.data?.tenant.id;
          if (!tenantId) throw Object.assign(new Error('The governed organization is not known yet.'), { message: 'The governed organization is not known yet.' });
          setRotated(await settingsRotateAllKeys(client, { body: { tenantId, confirm: true } }));
          setDone((p) => ({ ...p, 'Rotate every key': '✓ Done' }));
        },
      })],
    ['Purge sample data', 'Removes the synthetic household and sample facilities.', 'Purge', done['Purge sample data'] ?? '',
      'Removes the synthetic household and sample facilities. Real residents are not touched.',
      () => ask({
        sec: 'Danger Zone', title: 'Purge sample data?', btn: 'Purge',
        body: 'Removes the synthetic household and sample facilities. Real residents are not touched.',
        extra: <OrgFields onPick={(r) => { org.current = r; }} />,
        go: async () => {
          const r = await settingsPurgeSample(client, { body: { organizationId: needOrg(), confirm: true } });
          if (r.refused) throw Object.assign(new Error(r.reason ?? 'Refused'), { message: r.reason ?? 'Refused' });
          setDone((p) => ({ ...p, 'Purge sample data': r.live === false ? '✓ Checked (purge is switched off)' : '✓ Done' }));
        },
      })],
    // Restored from the Flutter console (no design control): POST /api/settings/reset.
    ['Reset every setting', 'Gate posture, notifications, retention and display back to their defaults. The profile stays.', 'Reset to defaults', done['Reset every setting'] ?? '',
      'Every tenant setting goes back to its default. The organization profile and keys are not touched. If the defaults would lower the default response level, only the owner can do this.',
      () => ask({
        sec: 'Danger Zone', title: 'Reset every setting to its default?', btn: 'Reset to defaults',
        body: 'Every tenant setting goes back to its default. The organization profile and keys are not touched. If the defaults would lower the default response level, only the owner can do this.',
        go: async () => {
          await settingsReset(client, { body: { confirm: true } });
          setDone((p) => ({ ...p, 'Reset every setting': '✓ Reset' }));
        },
      })],
  ];
  const hasAct = lens.lens === 'Operator';

  return (
    <div data-testid="settings-danger-zone" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;max-width:760px;')}>
      <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;margin-bottom:6px;flex-wrap:wrap;row-gap:8px;')}>
        <span style={sx('font-size:16px;font-weight:700;')}>Danger Zone</span>
        <span style={sx(chip('#B3261E', 'rgba(179,38,30,.14)'))}>IRREVERSIBLE</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.6;')}>
        {I('Deleting an organization removes its residents, conversations and trust ledger. The Truth Ledger entries survive, because an audit trail cannot be deleted by the party being audited.')}
      </div>
      <div style={sx('margin-top:14px;padding-top:13px;border-top:1px solid rgba(34,39,46,.09);')}>
        <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>What lives here</div>
        {ITEMS.map(([name, note, act, doneLabel, , start]) => (
          <div key={name} style={sx('display:flex;align-items:flex-start;gap:10px;padding:7px 0;border-top:1px solid rgba(34,39,46,.06);')}>
            <span style={sx('width:5px;height:5px;border-radius:50%;background:#54606C;margin-top:8px;flex:0 0 5px;')} />
            <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
              <span style={sx('display:block;font-size:16px;font-weight:600;color:#22272E;')}>{I(name)}</span>
              <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;line-height:1.5;')}>{I(note)}</span>
            </span>
            {hasAct && (
              <a
                href="#"
                aria-disabled={!canWrite || !!doneLabel || undefined}
                title={roReason ?? undefined}
                onClick={(e) => { pre(e); if (canWrite && !doneLabel) { org.current = null; reason.current = ''; real.current = false; start(); } }}
                style={sx('flex:0 0 auto;display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#B3261E;border:1px solid rgba(179,38,30,.4);')}
              >
                {I(doneLabel || act)}
              </a>
            )}
          </div>
        ))}
      </div>
      {rotated && (
        <div data-testid="rotated-keys" style={sx('margin-top:12px;border:1px solid rgba(179,38,30,.28);background:rgba(179,38,30,.05);border-radius:10px;padding:11px 13px;font-size:16px;color:#3C454F;line-height:1.5;')}>
          <b style={sx('color:#B3261E;')}>{rotated.warning}</b>
          {rotated.newKeys.map((k) => (
            <div key={k.keyId} style={sx('font-variant-numeric:tabular-nums;margin-top:4px;')}>{k.name + ': ' + k.secretKey}</div>
          ))}
          {rotated.lostAccess.length > 0 && <div style={sx('margin-top:6px;')}>{'Lost access until updated: ' + rotated.lostAccess.map((x) => x.name).join(', ')}</div>}
        </div>
      )}
      {deletions.error && <ErrLine error={deletions.error} />}
    </div>
  );
}
